import { createJsonQuery } from '@farfetched/core'
import { runtypeContract } from '@farfetched/runtypes'
import { filterListUrl } from 'src/features/filter-goods/api'
import { FilterGroup, IFilterGroup } from 'src/features/filter-goods/contract'
import { useEvent, useStore, useStoreMap } from 'effector-react'
import { Array } from 'runtypes'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { $goods, resetGood } from '@entities/goods/model'
import { ICatalogGoodBase, IGoodFilter } from '@entities/goods/contract'
import { $currentPath, onRouteChangeComplete } from '@entities/route/model'
import {
    $isOpenedFilterWindow,
    $isOpenedGroup,
    closeWindow,
    toggleOpenedWindow
} from 'src/features/filter-goods/model/core'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { orderBy } from 'lodash-es'
import { reduceSet } from 'src/shared/lib/reduce-set/model'
import { $isClient } from 'src/shared/model/client-status'
import { $currentGroupId, $groupMap, IGroupMap } from '@entities/groups/model'
import { $defaultPath } from 'src/features/filter-goods/model/categories'

export const filterListQuery = createJsonQuery({
    request: {
        method: 'GET',
        url: () => filterListUrl()
    },
    initialData: [],
    response: {
        contract: runtypeContract(Array(FilterGroup))
    }
})

export interface IGoodFilterExt extends IGoodFilter {
    customId: string
}

export interface IGoodFilterSelected extends IGoodFilterExt {
    isSelected: boolean
}

function toggleItem(source: string[], clock: string) {
    if (!source || source.length === 0) return [clock]
    if (source.indexOf(clock) >= 0) {
        return source.filter((x) => x !== clock)
    }
    return [...source, clock]
}

const getCustomFilterId = (filter: IGoodFilter) => `${filter.siteFilterID}_${filter.filterValue}`

export const resetGoodFilter = createEvent()

const $filterGroupList = filterListQuery.$data

const $filtersGoods = createStore<IGoodFilterSelected[]>([]).reset(resetGood).reset(resetGoodFilter)

//makers and out forms
const toggleOutForms = createEvent<string>()
const toggleMaker = createEvent<string>()

export const $outFormsGoods = createStore<string[]>([])
export const $makersGoods = createStore<string[]>([])

export const $selectedOutForms = createStore<string[]>([], { serialize: 'ignore' }).reset(resetGoodFilter)
export const $selectedMakers = createStore<string[]>([], { serialize: 'ignore' }).reset(resetGoodFilter)

const rebuildFilters = createEvent()

sample({
    clock: $goods,
    filter: $isClient,
    target: rebuildFilters
})

sample({
    clock: $currentPath,
    filter: $isClient,
    target: rebuildFilters
})

sample({
    clock: rebuildFilters,
    source: $goods,
    filter: $isClient,
    fn: (goods) => reduceSet(goods.map((x) => x.webData.outFormTitle)) as string[],
    target: $outFormsGoods
})

sample({
    clock: rebuildFilters,
    source: $goods,
    filter: $isClient,
    fn: (goods) => reduceSet(goods.map((x) => x.webData.makerTitle)) as string[],
    target: $makersGoods
})

sample({
    clock: toggleOutForms,
    source: $selectedOutForms,
    fn: toggleItem,
    target: $selectedOutForms
})

sample({
    clock: toggleMaker,
    source: $selectedMakers,
    fn: toggleItem,
    target: $selectedMakers
})
//end makers and out forms

export const togglePreviewSelectFiltersGoods = createEvent<IGoodFilter>()
export const toggleSelectFiltersGoods = createEvent<IGoodFilter>()

export const applySelectedFiltersGoods = createEvent()
const clearPreviewSelectFiltersGoods = createEvent()
const $previewSelectFiltersGoods = createStore<IGoodFilterExt[]>([], { serialize: 'ignore' })
    .reset(clearPreviewSelectFiltersGoods)
    .reset(toggleOpenedWindow)
    .reset(closeWindow)
    .reset(onRouteChangeComplete)

export const $selectedFiltersGoods = createStore<IGoodFilterExt[]>([], { serialize: 'ignore' })
    .reset(resetGoodFilter)
    .reset(onRouteChangeComplete)

// noinspection DuplicatedCode
function categoryFiltering({
    selectedGroupId,
    goods,
    groupMap
}: {
    selectedGroupId: number
    goods: ICatalogGoodBase[]
    groupMap: IGroupMap
}) {
    if (!selectedGroupId || selectedGroupId <= 0) return goods

    // берем товары только выделенной группы
    return goods.filter((good) => {
        return good.webData.goodGroups.some((gg) => {
            let currentGroup = groupMap[gg.clsGroupID]
            if (!currentGroup) {
                //console.log(`Good group id ${gg.clsGroupID} not found`)
            }
            if (currentGroup?.id === selectedGroupId) return true
            while (currentGroup?.parentClsGroupId) {
                currentGroup = groupMap[currentGroup.parentClsGroupId]
                if (currentGroup?.id === selectedGroupId) return true
            }
            return false
        })
    })
}

const generateFilters = (
    goods: ICatalogGoodBase[],
    selectedFilters: IGoodFilterExt[],
    currentGroupId: number,
    groupMap: IGroupMap
) => {
    if (!goods || goods.length <= 0) return []
    const goodsCategories = categoryFiltering({
        goods,
        selectedGroupId: currentGroupId,
        groupMap
    })
    const filters = goodsCategories.flatMap((x) => x.filters).filter((x) => !!x) as IGoodFilter[]

    const filtersExt: IGoodFilterSelected[] = filters.map((f) => ({
        siteFilterID: f.siteFilterID,
        filterValue: f.filterValue,
        customId: getCustomFilterId(f),
        isSelected: selectedFilters.some((x) => x.customId === getCustomFilterId(f))
    }))

    const uniqFilters = [...new Map(filtersExt.map((item) => [item.customId, item])).values()] as IGoodFilterSelected[]
    return orderBy(uniqFilters, ['filterValue', 'isSelected'], ['asc', 'desc'])
}

const updateFilters = (filtersGoods: IGoodFilterSelected[], selectedFilters: IGoodFilterExt[]) => {
    return filtersGoods?.map(
        (f): IGoodFilterSelected =>
            ({
                siteFilterID: f.siteFilterID,
                filterValue: f.filterValue,
                customId: getCustomFilterId(f),
                isSelected: selectedFilters.some((x) => x.customId === getCustomFilterId(f))
            } || [])
    )
}

const generateGoodsFiltersFx = createEffect<
    { goods: ICatalogGoodBase[]; selectedFilters: IGoodFilterExt[]; currentGroupId: number; groupMap: IGroupMap },
    IGoodFilterSelected[]
>({
    handler: ({ goods, selectedFilters, currentGroupId, groupMap }) =>
        generateFilters(goods, selectedFilters, currentGroupId, groupMap)
})

subscribeLoggerEffect(generateGoodsFiltersFx)

const generatePreviewGoodsFiltersFx = createEffect<
    {
        goods: ICatalogGoodBase[]
        selectedFilters: IGoodFilterSelected[]
        currentGroupId: number
        groupMap: IGroupMap
    },
    IGoodFilterSelected[]
>({
    handler: ({ goods, selectedFilters, currentGroupId, groupMap }) =>
        generateFilters(goods, selectedFilters, currentGroupId, groupMap)
})

subscribeLoggerEffect(generatePreviewGoodsFiltersFx)

sample({
    clock: generateGoodsFiltersFx.doneData,
    target: $filtersGoods
})

/**
 * start generate goods
 */
sample({
    clock: [onRouteChangeComplete, $goods, $currentGroupId],
    source: {
        goods: $goods,
        currentGroupId: $currentGroupId,
        groupMap: $groupMap
    },
    //filter: $isClient,
    fn: ({ goods, currentGroupId, groupMap }) => ({
        goods,
        currentGroupId,
        groupMap,
        selectedFilters: []
    }),
    target: [resetGoodFilter, generateGoodsFiltersFx]
})

/**
 * toggle filter element
 */
sample({
    clock: togglePreviewSelectFiltersGoods,
    source: $previewSelectFiltersGoods,
    fn: (source, clock) => {
        const customId = getCustomFilterId(clock)
        if (source?.some((x) => x.customId === customId)) {
            return source.filter((x) => x.customId !== customId)
        }
        return [
            ...source,
            {
                ...clock,
                customId
            }
        ]
    },
    target: $previewSelectFiltersGoods
})

/**
 * toggle seleted filters
 */
sample({
    clock: toggleSelectFiltersGoods,
    source: $selectedFiltersGoods,
    fn: (source, clock) => {
        const customId = getCustomFilterId(clock)
        if (source?.some((x) => x.customId === customId)) {
            return source.filter((x) => x.customId !== customId)
        }
        return [
            ...source,
            {
                ...clock,
                customId
            }
        ]
    },
    target: $selectedFiltersGoods
})

sample({
    clock: $selectedFiltersGoods,
    source: $filtersGoods,
    filter: $isOpenedGroup,
    fn: (filtersGoods, selectedFilters) => updateFilters(filtersGoods, selectedFilters),
    target: $filtersGoods
})

/**
 * apply preview filter (copy preview on real filters)
 */
sample({
    clock: applySelectedFiltersGoods,
    source: $previewSelectFiltersGoods,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    target: [$selectedFiltersGoods, $previewSelectFiltersGoods.reinit!, toggleOpenedWindow]
})

/**
 * create preview selected filter on open window with filter editor
 */

const onOpened = createEvent()

sample({
    clock: toggleOpenedWindow,
    filter: $isOpenedFilterWindow,
    source: $selectedFiltersGoods,
    target: [$previewSelectFiltersGoods, onOpened]
})

sample({
    clock: $previewSelectFiltersGoods,
    source: $filtersGoods,
    fn: (filtersGoods, selectedFilters) => updateFilters(filtersGoods, selectedFilters),
    target: $filtersGoods
})

sample({
    clock: onOpened,
    source: $currentPath,
    target: $defaultPath
})

sample({
    clock: onOpened,
    source: {
        goods: $goods,
        previewSelectFiltersGoods: $previewSelectFiltersGoods,
        currentGroupId: $currentGroupId,
        groupMap: $groupMap
    },
    fn: ({ goods, previewSelectFiltersGoods, currentGroupId, groupMap }) => ({
        goods: goods as ICatalogGoodBase[],
        selectedFilters: previewSelectFiltersGoods as IGoodFilterSelected[],
        currentGroupId,
        groupMap
    }),
    target: generatePreviewGoodsFiltersFx
})

sample({
    clock: generatePreviewGoodsFiltersFx.doneData,
    target: $filtersGoods
})

// Filter list details

/**
 * show filter event
 */
const showFilterGroup = createEvent<IFilterGroup | null>()
const cancelFilterGroup = createEvent()

/**
 * current filter
 */
const $selectedFilterGroup = createStore<IFilterGroup | null>(null, { serialize: 'ignore' })

const $lastSelectedFilter = createStore<IGoodFilterExt[]>([], { serialize: 'ignore' })

sample({
    clock: showFilterGroup,
    target: $selectedFilterGroup
})

// middleware state for filter group
/**
 * copy $previewSelectFiltersGoods to $lastSelectedFilter on showFilterGroup
 */
sample({
    clock: showFilterGroup,
    source: $previewSelectFiltersGoods,
    filter: (_, clock) => !!clock,
    target: $lastSelectedFilter
})

sample({
    clock: cancelFilterGroup,
    source: $lastSelectedFilter,
    target: $previewSelectFiltersGoods
})

sample({
    clock: cancelFilterGroup,
    fn: () => null,
    target: $selectedFilterGroup
})

export const useGoodFilterPreviewSelectedListById = (filterId: number): IGoodFilterSelected[] =>
    useStoreMap({
        store: $filtersGoods,
        keys: [filterId],
        fn: (filtersGoods, [id]) => {
            const filterArray: IGoodFilterSelected[] = filtersGoods?.filter((x) => x.siteFilterID === id)
            return orderBy(filterArray, ['isSelected', 'filterValue'], ['desc', 'asc'])
        }
    })

export const useGoodFilterPreviewSelectedListByIdAndSearch = (
    filterId: number,
    searchText: string
): IGoodFilterSelected[] =>
    useStoreMap({
        store: $filtersGoods,
        keys: [filterId, searchText],
        fn: (filtersGoods, [id, text]) => {
            const filterArray: IGoodFilterSelected[] = filtersGoods?.filter((x) => x.siteFilterID === id)

            if (!text) return filterArray

            return filterArray.filter((x) => x.filterValue.includes(text))
        }
    })
export const useSelectedOutForms = (id: string) =>
    useStoreMap({
        store: $selectedOutForms,
        keys: [id],
        fn: (outForms, [of]) => outForms.indexOf(of) >= 0
    })

export const useSelectedMakers = (id: string) =>
    useStoreMap({
        store: $selectedMakers,
        keys: [id],
        fn: (makers, [m]) => makers.indexOf(m) >= 0
    })

export const useLengthOutForms = () => useStoreMap($outFormsGoods, (state) => state.length)
export const useLengthMakers = () => useStoreMap($makersGoods, (state) => state.length)

export const useToggleOutForms = () => useEvent(toggleOutForms)
export const useToggleMakers = () => useEvent(toggleMaker)

export const usePreviewSelectionFilter = () => useStoreMap($previewSelectFiltersGoods, (state) => state.length)
export const useSelectionFilter = () => useStoreMap($selectedFiltersGoods, (state) => state.length)
export const useClearPreviewSelectFiltersGoods = () => useEvent(clearPreviewSelectFiltersGoods)
export const useTogglePreviewSelectFiltersGoods = () => useEvent(togglePreviewSelectFiltersGoods)
export const useApplySelectedFiltersGoods = () => useEvent(applySelectedFiltersGoods)
export const useShowFilterGroup = () => useEvent(showFilterGroup)
export const useSelectedFilterGroup = () => useStore($selectedFilterGroup)
export const useCancelModifyFilterGroup = () => useEvent(cancelFilterGroup)

export { $filterGroupList, $filtersGoods, $previewSelectFiltersGoods }
